import { dungApiThat, goiApi, goiApiKemEtag } from '../api/http';
import type { ApiError } from '../errors';
import { mayChuVaiTro } from '../../mock/vai-tro';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  PermissionPage,
  Role,
  RoleCreate,
  RolePage,
  UserRoles,
  UserRolesCoPhienBan,
} from '../../modules/vai-tro/types';

const GOC = '/api/v1/roles';

const duongDan = (id: string, phu?: string) =>
  `${GOC}/${encodeURIComponent(id)}${phu ? `/${phu}` : ''}`;

const vaiTroCuaNguoi = (userId: string) => `/api/v1/users/${encodeURIComponent(userId)}/roles`;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/**
 * Trang danh sách phải có mảng `items`. Thiếu là máy chủ trả sai hợp đồng (ví
 * dụ route chưa trỏ tới role-service mà tới một upstream khác): báo lỗi máy
 * chủ, đừng để view vỡ với câu `items is not iterable`.
 */
const kiemTrang = <T extends { items: unknown[] }>(trang: T): T => {
  if (!Array.isArray(trang?.items)) {
    throw { code: 'INTERNAL', status: 200, message: '' } satisfies ApiError;
  }
  return trang;
};

const TRANG_RONG: RolePage = { items: [], next_cursor: null };
const DANH_MUC_RONG: PermissionPage = { items: [], next_cursor: null };

/**
 * Truy cập vai trò lớp 2, danh mục quyền và vai trò của từng người (G2-D1-4,
 * ADR-0004), qua Gateway. `tenant_id` lấy từ token, không gửi tham số.
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/vai-tro.ts`).
 */
export const vaiTroRepository = {
  /**
   * `GET /api/v1/roles` — một trang, sắp theo `code`. `?mock=filtered-empty`
   * trả rỗng: màn lọc ở client, nên rỗng-vì-lọc hiện ra khi gõ ô tìm, đúng như
   * `document.repository`.
   */
  danhSach: ({ limit, cursor }: { limit: number; cursor?: string }): Promise<RolePage> => {
    if (dungApiThat()) return goiApi<RolePage>(GOC, { query: { limit, cursor } }).then(kiemTrang);
    if (mockState() === 'filtered-empty') return mockRequest(TRANG_RONG);
    return mockRequest(mayChuVaiTro.danhSach({ limit, cursor }), TRANG_RONG);
  },

  /**
   * `GET /api/v1/permissions` — một trang danh mục quyền gán được (tối đa 100 mỗi trang);
   * rỗng khi tổ chức chưa cài module nào.
   */
  danhMucQuyen: ({ limit, cursor }: { limit: number; cursor?: string }): Promise<PermissionPage> =>
    dungApiThat()
      ? goiApi<PermissionPage>('/api/v1/permissions', { query: { limit, cursor } }).then(kiemTrang)
      : mockRequest(mayChuVaiTro.danhMucQuyen(), DANH_MUC_RONG),

  /** `POST /api/v1/roles` — `khoa` là `Idempotency-Key`, mới cho mỗi lần bấm. */
  tao: (du: RoleCreate, khoa: string): Promise<Role> =>
    dungApiThat()
      ? goiApi<Role>(GOC, { method: 'POST', body: du, idempotencyKey: khoa })
      : giaLap(() => mayChuVaiTro.tao(du)),

  /** `PATCH /api/v1/roles/{id}` — chỉ đổi tên. */
  doiTen: (id: string, name: string): Promise<Role> =>
    dungApiThat()
      ? goiApi<Role>(duongDan(id), { method: 'PATCH', body: { name } })
      : giaLap(() => mayChuVaiTro.doiTen(id, name)),

  /** `PUT /api/v1/roles/{id}/permissions` — thay cả tập quyền. */
  ganQuyen: (id: string, permissions: string[]): Promise<Role> =>
    dungApiThat()
      ? goiApi<Role>(duongDan(id, 'permissions'), { method: 'PUT', body: { permissions } })
      : giaLap(() => mayChuVaiTro.ganQuyen(id, permissions)),

  /** `DELETE /api/v1/roles/{id}` → `204`; gỡ vai trò khỏi mọi người đang có. */
  xoa: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id), { method: 'DELETE' })
      : giaLap(() => mayChuVaiTro.xoa(id)),

  /** `GET /api/v1/users/{id}/roles` kèm `ETag`. */
  vaiTroCuaNguoi: async (userId: string): Promise<UserRolesCoPhienBan> => {
    if (!dungApiThat()) {
      return mockRequest(undefined).then(() => mayChuVaiTro.vaiTroCuaNguoi(userId));
    }
    const { du, etag } = await goiApiKemEtag<UserRoles>(vaiTroCuaNguoi(userId));
    return { ...du, etag };
  },

  /** `PUT /api/v1/users/{id}/roles` với `If-Match` — `412` khi người khác vừa đổi. */
  ganVaiTroChoNguoi: async (
    userId: string,
    roleIds: string[],
    etag: string,
  ): Promise<UserRolesCoPhienBan> => {
    if (!dungApiThat()) {
      return giaLap(() => mayChuVaiTro.ganVaiTroChoNguoi(userId, roleIds, etag));
    }
    const kq = await goiApiKemEtag<UserRoles>(vaiTroCuaNguoi(userId), {
      method: 'PUT',
      body: { role_ids: roleIds },
      ifMatch: etag,
    });
    return { ...kq.du, etag: kq.etag };
  },
};
